import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { apiFetch } from './api';
import { ApiError, type Campaign } from '../types/api';

/**
 * "Duplicate": copies a campaign (any status) into a new draft and opens
 * that draft in the editor. The API copies content, subject(s), preview
 * text, sender, audience, send mode (local time) and A/B auto-send — never
 * status, approval, schedule or statistics — so
 * the copy still has to be tested and approved like any other draft.
 */
export function useDuplicateCampaign() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ campaign: Campaign }>(`/campaigns/${id}/duplicate`, { method: 'POST' }),
    onSuccess: ({ campaign }) => {
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      navigate(`/kampagner/${campaign.id}`);
    },
  });
  const error = mutation.isError
    ? mutation.error instanceof ApiError && mutation.error.body.message
      ? mutation.error.body.message
      : 'The campaign could not be duplicated. Try again shortly.'
    : null;
  return { duplicate: mutation.mutate, pending: mutation.isPending, error };
}
